/**
 * React Query hooks for agent detail page data fetching.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getAgent,
  getAgentStats,
  getAgentMetrics,
  listAgentIssues,
  listAgentTraces,
  updateAgent,
  type AgentDetail,
  type AgentDetailStats,
  type AgentIssuesParams,
  type AgentMetricsResponse,
  type AgentTracesParams,
  type MetricsWindow,
  type PaginatedResponse,
  type SecurityIssueBackend,
  type TelemetryTrace,
} from "@/services/aiAgentsService";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a single AI agent by ID.
 */
export function useAgent(agentId: number) {
  const isApiReady = useApiReady();

  return useQuery<AgentDetail>({
    queryKey: ["ai-agent", agentId],
    queryFn: () => getAgent(agentId),
    enabled: agentId > 0 && isApiReady,
    staleTime: 1000 * 60 * 2, // 2 min — agent metadata rarely changes
  });
}

/**
 * Hook to fetch paginated security issues for an agent.
 */
export function useAgentIssues(agentId: number, params: AgentIssuesParams = {}) {
  const isApiReady = useApiReady();

  return useQuery<PaginatedResponse<SecurityIssueBackend>>({
    queryKey: ["ai-agent-issues", agentId, params],
    queryFn: () => listAgentIssues(agentId, params),
    enabled: agentId > 0 && isApiReady,
    staleTime: 1000 * 60, // 1 min
  });
}

/**
 * Hook to fetch paginated telemetry traces for an agent.
 */
export function useAgentTraces(agentId: number, params: AgentTracesParams = {}) {
  const isApiReady = useApiReady();

  return useQuery<PaginatedResponse<TelemetryTrace>>({
    queryKey: ["ai-agent-traces", agentId, params],
    queryFn: () => listAgentTraces(agentId, params),
    enabled: agentId > 0 && isApiReady,
    staleTime: 1000 * 60, // 1 min
  });
}

/**
 * Hook to fetch aggregated stats for an agent over a time window.
 */
export function useAgentStats(agentId: number, days: number = 30) {
  const isApiReady = useApiReady();

  return useQuery<AgentDetailStats>({
    queryKey: ["ai-agent-stats", agentId, days],
    queryFn: () => getAgentStats(agentId, days),
    enabled: agentId > 0 && isApiReady,
    staleTime: 60_000,
  });
}

/**
 * Hook to fetch performance metrics for an agent.
 */
export function useAgentMetrics(agentId: number, window: MetricsWindow = "7d") {
  const isApiReady = useApiReady();

  return useQuery<AgentMetricsResponse>({
    queryKey: ["ai-agent-metrics", agentId, window],
    queryFn: () => getAgentMetrics(agentId, window),
    enabled: agentId > 0 && isApiReady,
    staleTime: 60_000,
  });
}

/**
 * Hook to update an agent's name/description.
 */
export function useUpdateAgent(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name?: string; description?: string }) => updateAgent(agentId, data),
    onSettled: (_, error) => {
      if (error) {
        toast.error("Failed to update agent");
      } else {
        toast.success("Agent updated");
      }
      queryClient.invalidateQueries({ queryKey: ["ai-agent", agentId] });
      queryClient.invalidateQueries({ queryKey: ["ai-agents"] });
    },
  });
}
